import { lazy, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@assets/buttons-icons/add.svg?react';
import TaskIcon from '@lib/core/src/assets/empty-state-icons/tasks.svg?react';
import { EmptyState } from '@lib/core/src/components/EmptyState';
import { H2, SH2 } from '@lib/core/src/styledComponents/StyledComponents';
import { IconButton } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';

import { NylasGrantDtoProvider, TaskDto, TaskDtoStatus } from '@/api/generated';
import TaskRow from '@/components/TaskRow/TaskRow';
import RoutesMap from '@/enums/RoutesMap';
import useIntegrations from '@/hooks/Integrations/useIntegrations';
import useModal from '@/hooks/useModal/useModal';
import useTasks from '@/hooks/useTasks';
import { ViewAllButton } from '@/pages/HomePage/HomeStyles';
import { useIsSmallScreen } from '@/utils/mediaUtil';
import { TaskSortType, sortTasks } from '@/utils/tasksUtil';

import {
  HeaderContainer,
  Row,
  TasksContainer,
  HeaderImage,
  TasksContainerInner
} from './TasksViewStyles';

const LazyTaskModal = lazy(() => import('@/components/modals/TaskModal/TaskModal'));
const NUMBER_OF_TASKS_TO_SHOW = 10;

const TasksView = () => {
  const { taskList, refetchTasks } = useTasks(undefined, undefined, true, undefined, undefined);
  const navigate = useNavigate();
  const { userGrant } = useIntegrations();
  const { showModal } = useModal();
  const isSmallScreen = useIsSmallScreen();
  const onAddTask = useCallback(() => {
    const modal = showModal(LazyTaskModal, {
      onClose: () => {
        modal.hide();
      },
      onSubmit: () => {
        refetchTasks();
        modal.hide();
      }
    });
  }, [showModal, refetchTasks]);

  const pendingTasks = useMemo(
    () =>
      sortTasks(
        taskList.filter(task => task.status === TaskDtoStatus.pending),
        TaskSortType.NEW
      ),
    [taskList]
  );

  return (
    <>
      <HeaderContainer>
        <Row>
          <H2>What's on my table?</H2>
          <IconButton onClick={onAddTask}>
            <AddIcon width="16px" height="16px" />
          </IconButton>
          {!isSmallScreen && (
            <ViewAllButton onClick={() => navigate(RoutesMap.Tasks)}>View all</ViewAllButton>
          )}
        </Row>
        <SH2>{pendingTasks.length} Open Tasks</SH2>
        <HeaderImage />
      </HeaderContainer>
      {pendingTasks.length === 0 ? (
        <EmptyState text="No tasks" icon={<TaskIcon />} />
      ) : (
        <RenderTasks
          tasks={pendingTasks}
          userGrant={userGrant?.provider}
          refetchTasks={refetchTasks}
        />
      )}
    </>
  );
};

export default TasksView;

const RenderTasks = ({
  tasks,
  userGrant,
  refetchTasks
}: {
  tasks: TaskDto[];
  userGrant: NylasGrantDtoProvider | undefined;
  refetchTasks: () => void;
}) => (
  <TasksContainer>
    <TasksContainerInner>
      <AnimatePresence>
        {tasks.map((task, index) => {
          if (index < NUMBER_OF_TASKS_TO_SHOW) {
            return (
              <motion.div
                key={task.id}
                initial={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ duration: 0.2 }}
                style={{
                  width: '100%'
                }}
              >
                <TaskRow task={task} provider={userGrant} refetchTasks={refetchTasks} />
              </motion.div>
            );
          }
          return null;
        })}
      </AnimatePresence>
    </TasksContainerInner>
  </TasksContainer>
);
